import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaClock, FaCheckCircle, FaEdit, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import RichTextRenderer from '../common/RichTextRenderer';
import ImageUpload from '../common/ImageUpload';
import VideoRecorder from '../common/VideoRecorder';
import CollaboratorSelect from '../common/CollaboratorSelect';

const easing = [0.22, 1, 0.36, 1];
const fade = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: easing, staggerChildren: 0.06, delayChildren: 0.05 } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2, ease: easing } }
};
const child = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easing } }
};

/* --- Reusable editorial primitives --- */

const Eyebrow = ({ children, tone = 'primary' }) => (
  <div className="flex items-center gap-2.5 mb-3">
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full ${
        tone === 'mission' ? 'bg-mission' :
        tone === 'muted' ? 'bg-foreground/40' :
        'bg-primary'
      }`}
      aria-hidden="true"
    />
    <span
      className={`text-[10px] font-bold uppercase tracking-[0.22em] ${
        tone === 'mission' ? 'text-mission' :
        tone === 'muted' ? 'text-foreground/55' :
        'text-primary'
      }`}
    >
      {children}
    </span>
  </div>
);

const Notice = ({ eyebrow, title, body, tone = 'primary', icon, action }) => {
  const borderClass =
    tone === 'mission' ? 'border-mission' :
    tone === 'muted' ? 'border-foreground/30' :
    'border-primary';
  const iconClass =
    tone === 'mission' ? 'text-mission' :
    tone === 'muted' ? 'text-foreground/55' :
    'text-primary';

  return (
    <motion.div variants={child} className={`pl-5 border-l-2 ${borderClass}`}>
      <Eyebrow tone={tone}>{eyebrow}</Eyebrow>
      <div className="flex items-start gap-3">
        {icon && <div className={`mt-0.5 ${iconClass}`}>{icon}</div>}
        <div className="flex-1 min-w-0">
          <h4 className="font-display font-bold text-foreground text-lg leading-tight mb-1">{title}</h4>
          {body && <p className="text-[15px] text-foreground/70 leading-relaxed">{body}</p>}
          {action && <div className="mt-3">{action}</div>}
        </div>
      </div>
    </motion.div>
  );
};

const DescriptionBlock = ({ description }) => {
  if (!description) return null;
  return (
    <motion.section variants={child}>
      <Eyebrow tone="muted">Mission Description</Eyebrow>
      <div className="pl-5 border-l border-border">
        <RichTextRenderer
          content={description}
          className="lesson-content"
          fontSize="16px"
          lineHeight="1.65"
          maxWidth="none"
          allowInlineStyles={true}
        />
      </div>
    </motion.section>
  );
};

const EmbedBlock = ({ embedUrls, embedUrl }) => {
  const urls = embedUrls?.length > 0 ? embedUrls : (embedUrl ? [embedUrl] : []);
  if (urls.length === 0) return null;
  return (
    <motion.section variants={child}>
      <Eyebrow tone="muted">Embedded Content</Eyebrow>
      <div className="pl-5 border-l border-border space-y-4">
        {urls.map((url, i) => (
          <iframe
            key={i}
            src={url}
            title={`Embedded content ${i + 1}`}
            className="w-full border border-border rounded"
            style={{ height: '600px' }}
            allowFullScreen
            allow="autoplay; encrypted-media"
          />
        ))}
      </div>
    </motion.section>
  );
};

const RequirementsBlock = ({ content }) => {
  const items = [];
  if (content.linkRequired) items.push('Link submission is required');
  if (content.commentRequired) items.push('Comment is required');
  if (content.achievementImageRequired) items.push('Image is required');
  if (content.videoRequired) items.push('Video recording is required');
  if (items.length === 0) return null;

  return (
    <motion.section variants={child}>
      <Eyebrow tone="muted">Requirements</Eyebrow>
      <ul className="space-y-1.5 list-none p-0 m-0">
        {items.map((item, i) => (
          <li key={i} className="flex items-baseline gap-3 text-[15px] text-foreground/85">
            <span className="text-primary font-bold tabular-nums text-xs tracking-wider mt-0.5">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </motion.section>
  );
};

const Field = ({ label, required, optional, children, error, hint }) => (
  <div>
    <div className="flex items-baseline justify-between mb-2">
      <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/80 flex items-baseline gap-1.5">
        {label}
        {required && (
          <span className="text-mission text-[13px] leading-none translate-y-[1px]" aria-hidden="true">*</span>
        )}
      </label>
      <span className={`text-[10px] font-bold tabular-nums tracking-[0.18em] uppercase ${
        required ? 'text-mission' : 'text-foreground/40'
      }`}>
        {required ? 'Required' : optional ? 'Optional' : ''}
      </span>
    </div>
    {hint && (
      <p className="mb-2 text-[12px] text-foreground/55 leading-snug">{hint}</p>
    )}
    {children}
    {error && (
      <p className="mt-2 text-[12px] text-mission font-semibold flex items-center gap-1.5">
        <span className="inline-block w-1 h-1 rounded-full bg-mission" aria-hidden="true" />
        {error}
      </p>
    )}
  </div>
);

// Visible field surface: filled tint, 1px bottom rule that thickens to primary on focus,
// soft inset feel without looking like a corporate form input.
const inputBase =
  'w-full bg-muted border-0 border-b-[1.5px] border-foreground/15 ' +
  'focus:border-primary focus:bg-muted focus:ring-0 focus:outline-none ' +
  'px-3.5 py-3 text-[15px] text-foreground placeholder:text-foreground/35 ' +
  'rounded-md transition-all duration-200';

/* --- The component --- */

const SubmissionFormContent = ({
  userCurrentContent,
  submissionStatus,
  submissionData,
  setSubmissionData,
  urlError,
  submissionStatusLoading,
  isSubmitting,
  isWaitingForStatus,
  isViewingBackwards,
  handleUrlChange,
  getCurrentSubmissionStatus,
  handleSubmitSubmission,
  userSubmissionStatus,
  collaborators,
  setCollaborators,
  tokenConfig
}) => {
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (isViewingBackwards && userCurrentContent && userSubmissionStatus) {
      const status = userSubmissionStatus[userCurrentContent._id];
      if (status?.hasSubmission && ['rejected', 'more_info_needed'].includes(status.submission.status)) {
        const sub = status.submission;
        setSubmissionData({
          link: sub.link || '',
          comment: sub.comment || '',
          achievementImages: sub.achievementImages?.length ? sub.achievementImages : (sub.achievementImage ? [sub.achievementImage] : []),
          fullName: sub.fullName || '',
          university: sub.university || '',
          video: sub.videoUrl || ''
        });
      }
    }
  }, [isViewingBackwards, userCurrentContent, userSubmissionStatus, setSubmissionData]);

  // Submitted-record sub-component reused across read-only states
  const SubmittedRecord = ({ submission, headline, tone, icon }) => (
    <motion.section variants={child}>
      <Eyebrow tone={tone}>Submission</Eyebrow>
      <div className="pl-5 border-l-2 border-border">
        <div className="flex items-start gap-3 mb-4">
          {icon}
          <h4 className="font-display font-bold text-foreground text-xl leading-tight">{headline}</h4>
        </div>

        {submission.adminComment && (
          <div className="mb-4 bg-foreground/[0.03] p-4 border-l-2 border-primary">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary mb-1">
              Feedback
            </div>
            <p className="text-[14px] text-foreground/85 leading-relaxed">{submission.adminComment}</p>
          </div>
        )}

        <dl className="space-y-3 text-[14px]">
          {submission.link && (
            <div>
              <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/55 mb-1">Link</dt>
              <dd>
                <a
                  href={submission.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground hover:text-primary transition-colors break-all underline decoration-primary/40 underline-offset-2"
                >
                  {submission.link}
                </a>
              </dd>
            </div>
          )}
          {submission.comment && (
            <div>
              <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/55 mb-1">Comment</dt>
              <dd className="text-foreground/85 leading-relaxed">{submission.comment}</dd>
            </div>
          )}
          {submission.achievementImages?.length > 0 && (
            <div>
              <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/55 mb-2">Screenshots</dt>
              <dd className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {submission.achievementImages.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`Screenshot ${i + 1}`}
                    className="w-full h-32 object-cover"
                    style={{ borderRadius: '3px' }}
                  />
                ))}
              </dd>
            </div>
          )}
          {submission.videoUrl && (
            <div>
              <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/55 mb-2">Video</dt>
              <dd>
                <video src={submission.videoUrl} controls playsInline className="w-full max-h-80 bg-black" style={{ borderRadius: '3px' }} />
              </dd>
            </div>
          )}
          <div className="pt-3 border-t border-border/60 flex items-baseline gap-6 text-[11px] tabular-nums tracking-wide text-foreground/55">
            <span>
              <span className="font-bold uppercase tracking-[0.18em] mr-1.5">Submitted</span>
              {new Date(submission.submittedAt).toLocaleDateString()}
            </span>
            {submission.reviewedAt && (
              <span>
                <span className="font-bold uppercase tracking-[0.18em] mr-1.5">Reviewed</span>
                {new Date(submission.reviewedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </dl>
      </div>
    </motion.section>
  );

  /* --- Read-only review state (viewing backwards, not needing resubmit) --- */
  if (isViewingBackwards) {
    const backwardsStatus = userSubmissionStatus?.[userCurrentContent?._id];
    const backwardsSubmission = backwardsStatus?.hasSubmission ? backwardsStatus.submission : null;
    const needsResubmission = backwardsSubmission && ['rejected', 'more_info_needed'].includes(backwardsSubmission.status);

    if (!needsResubmission) {
      const isApproved = backwardsSubmission?.status === 'approved';
      const isPending = backwardsSubmission?.status === 'pending';
      return (
        <article className="max-w-5xl mx-auto">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={userCurrentContent?._id || 'mission'}
              variants={fade}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="space-y-4"
            >
              <Notice
                eyebrow="Reviewing"
                title="Reviewing Previous Mission"
                body="You are viewing a previous mission in read-only mode."
                tone="muted"
              />
              <DescriptionBlock description={userCurrentContent.description} />
              <EmbedBlock embedUrls={userCurrentContent.embedUrls} embedUrl={userCurrentContent.embedUrl} />
              {backwardsSubmission && (
                <SubmittedRecord
                  submission={backwardsSubmission}
                  headline={isApproved ? 'Mission Approved!' : 'Submission Under Review'}
                  tone={isApproved ? 'primary' : 'muted'}
                  icon={
                    isApproved
                      ? <FaCheckCircle className="w-5 h-5 text-primary mt-1" />
                      : <FaClock className="w-5 h-5 text-foreground/55 mt-1" />
                  }
                />
              )}
            </motion.div>
          </AnimatePresence>
        </article>
      );
    }
  }

  /* --- Submitting / waiting for status --- */
  if (isSubmitting || isWaitingForStatus) {
    return (
      <article className="max-w-5xl mx-auto">
        <motion.div variants={fade} initial="hidden" animate="visible" className="space-y-4">
          <DescriptionBlock description={userCurrentContent.description} />
          <EmbedBlock embedUrls={userCurrentContent.embedUrls} embedUrl={userCurrentContent.embedUrl} />
          <motion.section variants={child} className="pl-5 border-l-2 border-primary">
            <Eyebrow tone="primary">Submitting</Eyebrow>
            <div className="flex items-center gap-3">
              <svg className="animate-spin h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-[15px] text-foreground/80">Submitting Your Mission... Please wait while we process your submission.</p>
            </div>
          </motion.section>
        </motion.div>
      </article>
    );
  }

  /* --- Status loading --- */
  if (submissionStatusLoading) {
    return (
      <article className="max-w-5xl mx-auto">
        <div className="space-y-4">
          <DescriptionBlock description={userCurrentContent.description} />
          <EmbedBlock embedUrls={userCurrentContent.embedUrls} embedUrl={userCurrentContent.embedUrl} />
          <div className="pl-5 border-l-2 border-border">
            <Eyebrow tone="muted">Loading</Eyebrow>
            <p className="text-[15px] text-foreground/70">Loading Mission Status... Please wait while we check your submission status.</p>
          </div>
        </div>
      </article>
    );
  }

  /* --- Already submitted (approved or pending, not editing) --- */
  const currentSubmissionStatus = isViewingBackwards
    ? userSubmissionStatus?.[userCurrentContent?._id] || null
    : getCurrentSubmissionStatus();

  if (currentSubmissionStatus && currentSubmissionStatus.hasSubmission &&
      ['approved', 'pending'].includes(currentSubmissionStatus.submission.status)) {
    const { submission } = currentSubmissionStatus;

    if (!(submission.status === 'pending' && isEditing)) {
      const isApproved = submission.status === 'approved';
      const isPending = submission.status === 'pending';
      return (
        <article className="max-w-5xl mx-auto">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`${userCurrentContent?._id}-${submission.status}`}
              variants={fade}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="space-y-4"
            >
              <DescriptionBlock description={userCurrentContent.description} />
              <EmbedBlock embedUrls={userCurrentContent.embedUrls} embedUrl={userCurrentContent.embedUrl} />

              <SubmittedRecord
                submission={submission}
                headline={isApproved ? 'Mission Approved!' : 'Submission Under Review'}
                tone={isApproved ? 'primary' : 'muted'}
                icon={
                  isApproved
                    ? <FaCheckCircle className="w-5 h-5 text-primary mt-1" />
                    : <FaClock className="w-5 h-5 text-foreground/55 mt-1" />
                }
              />

              {isPending && (
                <motion.div variants={child}>
                  <button
                    onClick={() => {
                      setSubmissionData({
                        link: submission.link || '',
                        comment: submission.comment || '',
                        achievementImages: submission.achievementImages?.length ? submission.achievementImages : (submission.achievementImage ? [submission.achievementImage] : []),
                        video: submission.videoUrl || ''
                      });
                      setIsEditing(true);
                    }}
                    className="group inline-flex items-center gap-2 text-[13px] font-semibold text-foreground/70 hover:text-primary transition-colors"
                  >
                    <FaEdit className="w-3.5 h-3.5" />
                    <span>Edit submission</span>
                  </button>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </article>
      );
    }
  }

  /* --- Submission form (new, resubmission, or editing pending) --- */
  const isResubmission = currentSubmissionStatus && currentSubmissionStatus.hasSubmission &&
    ['rejected', 'more_info_needed'].includes(currentSubmissionStatus.submission.status);
  const isEditingExisting = isEditing && currentSubmissionStatus?.hasSubmission && currentSubmissionStatus.submission.status === 'pending';
  const previousSubmission = isResubmission ? currentSubmissionStatus.submission : (isEditingExisting ? currentSubmissionStatus.submission : null);

  return (
    <article className="max-w-5xl mx-auto">
      <motion.div variants={fade} initial="hidden" animate="visible" className="space-y-4">

        {/* Rejected — directive, not apologetic */}
        {isResubmission && previousSubmission.status === 'rejected' && previousSubmission.adminComment && (
          <Notice
            eyebrow="Needs revision"
            title="Your last submission was returned. Revise the work and send it again."
            body={previousSubmission.adminComment}
            tone="mission"
            icon={<FaExclamationTriangle className="w-4 h-4" />}
          />
        )}

        {/* More info requested */}
        {isResubmission && previousSubmission.status === 'more_info_needed' && previousSubmission.adminComment && (
          <Notice
            eyebrow="More information requested"
            title="A reviewer asked for additional detail."
            body={previousSubmission.adminComment}
            tone="primary"
            icon={<FaInfoCircle className="w-4 h-4" />}
          />
        )}

        {/* Editing indicator */}
        {isEditingExisting && (
          <Notice
            eyebrow="Editing"
            title="You're updating an existing submission."
            tone="muted"
            icon={<FaEdit className="w-4 h-4" />}
            action={
              <button
                onClick={() => setIsEditing(false)}
                className="text-[12px] font-semibold text-foreground/55 hover:text-foreground underline-offset-4 hover:underline transition-colors"
              >
                Cancel and keep what's submitted
              </button>
            }
          />
        )}

        <DescriptionBlock description={userCurrentContent.description} />
        <EmbedBlock embedUrls={userCurrentContent.embedUrls} embedUrl={userCurrentContent.embedUrl} />
        <RequirementsBlock content={userCurrentContent} />

        {/* The form */}
        <motion.section variants={child} className="space-y-4">
          <Eyebrow tone="mission">Your submission</Eyebrow>

          {/* Link */}
          <Field
            label="Link"
            required={userCurrentContent.linkRequired}
            optional={!userCurrentContent.linkRequired}
            error={urlError}
          >
            <input
              type="url"
              value={submissionData.link}
              onChange={handleUrlChange}
              placeholder="https://your-project-link.com"
              className={`${inputBase} ${urlError ? 'border-mission focus:border-mission' : ''}`}
              required={userCurrentContent.linkRequired}
            />
          </Field>

          {/* Comment */}
          <Field
            label="Comment"
            required={userCurrentContent.commentRequired}
            optional={!userCurrentContent.commentRequired}
          >
            <textarea
              value={submissionData.comment}
              onChange={(e) => setSubmissionData({ ...submissionData, comment: e.target.value })}
              placeholder="Describe your submission..."
              rows={4}
              className={`${inputBase} resize-none`}
              required={userCurrentContent.commentRequired}
            />
          </Field>

          {/* Images */}
          <Field
            label="Screenshots"
            required={userCurrentContent.achievementImageRequired}
            optional={!userCurrentContent.achievementImageRequired}
          >
            <ImageUpload
              value={submissionData.achievementImages}
              onChange={(images) => setSubmissionData({ ...submissionData, achievementImages: images })}
              required={userCurrentContent.achievementImageRequired}
              multiple={true}
            />
          </Field>

          {/* Video */}
          {userCurrentContent.videoRequired && (
            <Field label="Video Recording" required>
              <VideoRecorder
                value={submissionData.video}
                onChange={(video) => setSubmissionData(prev => ({ ...prev, video }))}
                instructions={userCurrentContent.videoInstructions}
              />
            </Field>
          )}

          {/* University */}
          {userCurrentContent.universityRequired && (
            <Field
              label="University"
              required={userCurrentContent.universityRequired}
            >
              <input
                type="text"
                value={submissionData.university}
                onChange={(e) => setSubmissionData({ ...submissionData, university: e.target.value })}
                placeholder="Enter your university"
                className={inputBase}
                required={userCurrentContent.universityRequired}
              />
            </Field>
          )}

          {/* Collaborators */}
          {!isEditingExisting && !isResubmission && tokenConfig && userCurrentContent?.collaboratorsEnabled !== false && (
            <Field
              label="Collaborators"
              optional
            >
              <CollaboratorSelect
                value={collaborators || []}
                onChange={setCollaborators}
                tokenConfig={tokenConfig}
              />
            </Field>
          )}

          {/* Inline Update for editing pending */}
          {isEditingExisting && handleSubmitSubmission && (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                handleSubmitSubmission();
                setIsEditing(false);
              }}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-[13px] font-bold tracking-wide transition-opacity ${
                isSubmitting
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'text-mission-foreground bg-mission hover:opacity-90'
              }`}
              style={{ borderRadius: '2px' }}
            >
              {isSubmitting ? 'Submitting...' : 'Update Submission'}
            </button>
          )}
        </motion.section>
      </motion.div>
    </article>
  );
};

export default SubmissionFormContent;
